import { useTranslation } from "react-i18next";
import type { GroupMemberResponseDto } from "@tabipla/shared";

interface MemberAvatarStackProps {
  members: GroupMemberResponseDto[];
  maxDisplay?: number;
  onPress?: () => void;
}

/**
 * Get initials from a name (first 2 characters for Japanese, first letter for others)
 */
function getInitials(name: string): string {
  if (!name?.trim()) return "?";
  // For Japanese names, use first 2 characters
  if (/[\u3040-\u309F\u30A0-\u30FF\u4E00-\u9FAF]/.test(name)) {
    return name.slice(0, 2);
  }
  // For other names, use first letter of each word (max 2)
  return name
    .split(" ")
    .filter((n) => n.length > 0)
    .map((n) => n[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

/**
 * Get a consistent color for an avatar based on the name
 */
function getAvatarColor(name: string): string {
  const colors = [
    "bg-blue-500",
    "bg-green-500",
    "bg-yellow-500",
    "bg-purple-500",
    "bg-pink-500",
    "bg-indigo-500",
    "bg-teal-500",
    "bg-orange-500",
  ];
  const hash = name.split("").reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return colors[hash % colors.length];
}

/**
 * Render a single member avatar with fallback hierarchy:
 * 1. Profile image (from social login)
 * 2. Custom emoji + color
 * 3. Generated initials + color
 */
function MemberAvatar({ member, zIndex }: { member: GroupMemberResponseDto; zIndex: number }) {
  // Priority 1: Profile image
  if (member.image) {
    return (
      <img
        src={member.image}
        alt={member.memberName}
        className="h-8 w-8 rounded-full border-2 border-background object-cover"
        style={{ zIndex }}
        title={member.memberName}
      />
    );
  }

  // Priority 2: Custom avatar emoji + color
  if (member.avatarEmoji && member.avatarColor) {
    return (
      <div
        className={`flex h-8 w-8 items-center justify-center rounded-full border-2 border-background bg-gradient-to-br text-sm ${member.avatarColor}`}
        style={{ zIndex }}
        title={member.memberName}
      >
        {member.avatarEmoji}
      </div>
    );
  }

  // Priority 3: Generated initials + color
  return (
    <div
      className={`flex h-8 w-8 items-center justify-center rounded-full border-2 border-background text-xs font-semibold text-white ${getAvatarColor(member.memberName)}`}
      style={{ zIndex }}
      title={member.memberName}
    >
      {getInitials(member.memberName)}
    </div>
  );
}

/**
 * Stacked avatar display for group members.
 * Shows up to maxDisplay avatars with overlap, plus a "+N" indicator for remaining members.
 */
export function MemberAvatarStack({ members, maxDisplay = 3, onPress }: MemberAvatarStackProps) {
  const { t } = useTranslation();

  const displayedMembers = members.slice(0, maxDisplay);
  const remainingCount = Math.max(0, members.length - maxDisplay);

  return (
    <button
      type="button"
      onClick={onPress}
      className="flex items-center gap-1 rounded-full px-2 py-1 transition-colors hover:bg-accent"
      aria-label={t("groups.viewMembers", "メンバーを見る")}
    >
      {/* Stacked avatars */}
      <div className="flex -space-x-2">
        {displayedMembers.map((member, index) => (
          <MemberAvatar
            key={`${member.memberType}:${member.memberId}`}
            member={member}
            zIndex={maxDisplay - index}
          />
        ))}
      </div>

      {/* Remaining count */}
      {remainingCount > 0 && (
        <span className="ml-1 text-sm text-muted-foreground">+{remainingCount}</span>
      )}

      {/* Total count label */}
      <span className="ml-1 text-sm text-muted-foreground">
        {members.length}
        {t("groups.members", "人")}
      </span>
    </button>
  );
}
